// evaluator-kinds.js - who made a judgement, and at what resolution.
//
// WHY THIS EXISTS. Until now the only record of who evaluated a player was the
// `team_code` column, which carries five different meanings at once: a literal
// team code, a per-coach sentinel (`staff:<id>`), and three author sentinels
// (DIRECTOR, CODIRECTOR, COORDINATOR). Every consumer that cared had to
// re-derive the distinction with its own string tests, and they did not all
// agree. This is that derivation, written once.
//
// THE RESOLUTION IS THE IMPORTANT PART. A coach marks twenty-five criteria and
// means twenty-five things. A coordinator's fast pass taps once per pillar, and
// the criterion rows it writes are six copies of that one tap: they carry the
// right WEIGHT for a pillar average, and they carry no per-skill information at
// all. Measured on real data, every one of the 28 player-pillars from the first
// fast pass held a single distinct level. Showing those as per-skill results,
// or averaging them into per-skill means, invents detail nobody observed.
//
// So each kind declares its scope, and `scope === 'pillar'` is the flag that
// keeps a fast pass out of every per-skill surface while leaving it in every
// pillar one.

/**
 * Every way a judgement about a player enters the system.
 *
 * `scope`:
 *   'criterion'   - a real mark per skill. Safe everywhere.
 *   'pillar'      - one judgement per pillar, stored as criterion rows to carry
 *                   the correct weight. Must never be shown or aggregated per
 *                   skill.
 *   'measurement' - a number on its own scale (seconds, repetitions), not 1-5.
 */
export const EVALUATOR_KINDS = [
  { key: 'director',   label: 'Director',        scope: 'criterion',   weight: 1.5, inEvaluations: true },
  { key: 'codirector', label: 'Co-director',     scope: 'criterion',   weight: 1,   inEvaluations: true },
  { key: 'staff',      label: 'Coach (staff)',   scope: 'criterion',   weight: 1,   inEvaluations: true },
  { key: 'team',       label: 'Coach (team)',    scope: 'criterion',   weight: 1,   inEvaluations: true },
  { key: 'fast',       label: 'Fast evaluation', scope: 'pillar',      weight: 1,   inEvaluations: true },
  { key: 'baseline',   label: 'Baseline testing', scope: 'measurement', weight: 1,  inEvaluations: false },
  { key: 'self',       label: 'Self-evaluation', scope: 'criterion',   weight: 1,   inEvaluations: false },
];

/** The kinds that can appear on an `evaluations` row, in reporting order. */
export const EVALUATION_KINDS = EVALUATOR_KINDS.filter(k => k.inEvaluations);

const BY_KEY = new Map(EVALUATOR_KINDS.map(k => [k.key, k]));

/** The sentinels `team_code` carries, kept here so nothing re-derives them. */
export const SENTINELS = {
  director:    'DIRECTOR',
  codirector:  'CODIRECTOR',
  coordinator: 'COORDINATOR',
  staffPrefix: 'staff:',
};

/**
 * Which kind of evaluator produced this evaluation.
 *
 * Reads the stored `evaluator_kind` when there is one, and otherwise falls back
 * to reading `team_code`, so rows written before the column existed still
 * classify correctly and no backfill is load-bearing.
 */
export function evaluatorKindOf(evaluation) {
  const stored = evaluation?.evaluator_kind;
  if (stored && BY_KEY.has(stored)) return stored;

  const code = String(evaluation?.team_code ?? '');
  if (code === SENTINELS.director)    return 'director';
  if (code === SENTINELS.codirector)  return 'codirector';
  if (code === SENTINELS.coordinator) return 'fast';
  if (code.startsWith(SENTINELS.staffPrefix)) return 'staff';
  if (evaluation?.author_staff_id)    return 'staff';
  return 'team';
}

/**
 * At what resolution this evaluation was actually made.
 *
 * RESOLUTION FOLLOWS THE METHOD, NOT THE PERSON. An evaluation that belongs to
 * a grading pass is a PLACEMENT: one tap per pillar, resolved per criterion
 * against the band that pass set. It carries no per-child criterion
 * observation whoever signed it, so `pass_id` decides this before the
 * evaluator's kind is even consulted.
 *
 * That order matters. While only coordinators ran passes, the kind happened to
 * give the right answer ('fast' is pillar-scope). The moment the director
 * signed one, the kind said 'director', the scope came back 'criterion', and
 * five placements went into the per-skill means at 1.5x weight carrying detail
 * nobody observed. That is exactly what the old Team review screen did, so
 * asking the person rather than the method is the bug, not the mapping.
 */
export function scopeOf(evaluation) {
  const stored = evaluation?.rating_scope;
  if (stored === 'criterion' || stored === 'pillar') return stored;
  if (evaluation?.pass_id) return 'pillar';
  return BY_KEY.get(evaluatorKindOf(evaluation))?.scope === 'pillar' ? 'pillar' : 'criterion';
}

/**
 * Does this evaluation carry real per-skill information?
 *
 * The one question every per-skill surface has to ask: the workbook's skill
 * columns, the skill breakdown on a player page, the per-skill means in
 * `player_skill_summary`. A false here does NOT mean "ignore this evaluation",
 * only "do not read it skill by skill". Its pillar average still counts.
 */
export function hasSkillDetail(evaluation) {
  return scopeOf(evaluation) === 'criterion';
}

/** Display label for a kind key, for the workbook and the screens. */
export function labelFor(kind) {
  return BY_KEY.get(kind)?.label || 'Unknown';
}

/** Scoring weight for a kind. The director leads but does not fully double. */
export function weightFor(kind) {
  return BY_KEY.get(kind)?.weight ?? 1;
}

/**
 * A stable identity for the PERSON, for grouping an export or bucketing the
 * bias correction. A coach with a personal staff code is one rater wherever
 * they coach; a team code is one rater per team.
 */
export function raterIdOf(evaluation) {
  if (evaluation?.author_staff_id) return `staff:${evaluation.author_staff_id}`;
  return String(evaluation?.team_code ?? 'unknown');
}
